import { BaseStore } from "stores/base_store"
import { runInAction, makeAutoObservable } from "mobx"
import { api_get, api_post } from "api/utils"
import { CursorType, PagedResult } from "utils/types"
import { UserProps } from "stores/user_store"


export type FHRequestType = {
    key: string;
    ogkey: string;
    cls: string;
    variant: string; // this will be empty on requests
    user: string;
    uuid: string;
    uniq: string;
    identity: string;
    title: string;
    project: string;
    content: string;
    content_type: string;
    status: number;
    source: number;
    price: number;
    pay_via_shift: boolean;
    task_credits: number;
    claimed_identity: string;
    claimed_by: string;
    claimed: boolean;
    date_claimed: string;
    time_of_first_response: string;
    completed: boolean;

    needs_manager_attention: boolean;

    solved: boolean;
    date_solved: string;
    solved_by: string;

    archived: boolean;
    client_archived: boolean;
    assistant_flagged: boolean;

    // invited_user: string;
    // invited_uuid: string;

    last_action_type: string;
    last_action_summary: string;
    last_action_date: string;
    last_action_identity: string;

    clock_total_seconds: number;

    date_created: string;
    date_updated: string;
    updateProps?: (attributes: FHRequestType) => void;
};

// Domain object FHRequest
export class FHRequest implements FHRequestType {
    key = "";
    ogkey = "";
    cls = "";
    variant = "";
    user = "";
    uuid = "";
    uniq = "";
    project = "";
    title = "";
    identity = "";
    content = "";
    content_type = "";
    status = 0;
    source = 0;
    price = 0;
    pay_via_shift = false;
    task_credits = 0;
    claimed_identity = "";
    claimed_by = "";
    claimed = false;
    date_claimed = "";
    time_of_first_response = "";
    completed = false;

    // invited_user: string;
    // invited_uuid: string;


    needs_manager_attention = false;

    solved = false;
    date_solved = "";
    solved_by = "";
    archived = false;
    client_archived = false;
    assistant_flagged = false;

    clock_total_seconds = 0;
    last_action_type = "";
    last_action_summary = "";
    last_action_date = "";
    last_action_identity = "";

    date_created = "";
    date_updated = "";

    constructor(attributes: FHRequestType) {
        makeAutoObservable(this, {
            updateProps: false
        })
        if (attributes) {
            this.updateProps(attributes);
        }
    }

    updateProps(attributes: FHRequestType) {
        runInAction(() => {
            Object.assign(this, attributes);
        });
    }
}


export class FHRequestStore extends BaseStore<FHRequestType, FHRequest> {

    FETCHED_REQUESTS = [] as string[];
    _fetch_queue = [] as string[];
    _fetch_queue_timer = 0;

    _add_to_fetch_queue(key: string) {
        if(this._fetch_queue.includes(key))
            return;
        this._fetch_queue.push(key);
    }

    async doFetchQueue() {
        const items = this._fetch_queue;
        this._fetch_queue = [];
        if(items.length === 0)
            return;


        const keys = items.join(",");
        const params = { keys };
        const response = await api_get("/request/list/by-keys", params);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(this._fetch_queue.length > 0) {
                this._fetch_queue_timer = window.setTimeout(() => {
                    this.doFetchQueue();
                }, 500);
            }
            else {
                this._fetch_queue_timer = 0;
            }


        }
        return null;

    }

    async fetchRequest(uuid: string) {

        if(this.FETCHED_REQUESTS.includes(uuid))
            return null;

        this.FETCHED_REQUESTS.push(uuid);
        this._add_to_fetch_queue(uuid);
        if(!this._fetch_queue_timer)  {
            this._fetch_queue_timer = window.setTimeout(() => {
                this.doFetchQueue();
            }, 100);
        }
    }



    async fetchUserInfoRequest(uuid: string): Promise<UserProps | null> {
        const response = await api_get(`/request/user-info?request=${uuid}`);
        if(response.ok) {
            const data = await response.json() as UserProps;
            return data;
        }
        return null;
    }


    async fetchListByUser(cursor?: CursorType): Promise<string>  {
        const url = "/request/list/by-user" + (cursor ? `?cursor=${cursor}` : '');
        const response = await api_get(url);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            // if(data?.cursor) {
            //     this.fetchListByUser(data.cursor);
            // }
            // This returns as possibly incomplete list due to the cursor,
            // but FHRequestStore.items is complete when .then() is called
            // so you can/should just use that.
            return data?.cursor;
        }
        return '';
    };


    async fetchListByProject(project: string, cursor?: CursorType): Promise<FHRequestType[]>  {
        const url = "/request/list/by-project";
        const data = { project, cursor: cursor || ""};
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(data?.cursor) {
                this.fetchListByProject(project, data.cursor);
            }
            // This returns as possibly incomplete list due to the cursor,
            // but FHRequestStore.items is complete when .then() is called
            // so you can/should just use that.
            return data?.cursor;
        }
        return [];
    };


    async fetchListAvailable(workKey: string, cursor?: CursorType): Promise<FHRequestType[]>  {
        const url = "/request/list/available";
        const data = { identity: workKey, cursor: cursor || ""};
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            // this has too many results, not just getting first page for now
            // if(data?.cursor) {
            //     this.fetchListAvailable(workKey, data.cursor);
            // }
            return data.items;
        }
        return [];
    };

    async supportFetchRequests(workKey: string, lookup_identity: string, cursor = "") {
        const params = { identity: workKey, lookup_identity, cursor };
        return api_get("/request/support/list/by-identity", params)
        .then(async (res) => {
            if (res.ok) {
                const data = await res.json();
                this.receiveUpdate(data.items);
                return data as PagedResult<FHRequestType>;
            }
        });
    }

    async fetchListSupportAttention(workKey: string, cursor?: CursorType): Promise<FHRequestType[]>  {
        const url = "/request/list/support/attention";
        const data = { identity: workKey, cursor: cursor || ""};
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(data?.cursor) {
                this.fetchListSupportAttention(workKey, data.cursor);
            }
            return data.items;
        }
        return [];
    };

    async fetchListClaimed(workKey: string, cursor?: CursorType): Promise<FHRequestType[]>  {
        const url = "/request/list/claimed";
        const data = { identity: workKey, cursor: cursor || ""};
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(data?.cursor) {
                this.fetchListClaimed(workKey, data.cursor);
            }
            // This returns as possibly incomplete list due to the cursor,
            // but FHRequestStore.items is complete when .then() is called
            // so you can/should just use that.
            return data.items;
        }
        return [];
    };

    async fetchListClosed(workKey: string, cursor?: CursorType): Promise<FHRequestType[]>  {
        const url = "/request/list/closed";
        const data = { identity: workKey, cursor: cursor || ""};
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            // if(data?.cursor) {
            //     this.fetchListClosed(workKey, data.cursor);
            // }
            // This returns as possibly incomplete list due to the cursor,
            // but FHRequestStore.items is complete when .then() is called
            // so you can/should just use that.
            return data.items;
        }
        return [];
    };



    async fetchListAllOpen(workKey: string, cursor?: CursorType): Promise<string|null>  {
        const url = "/request/list/all/open";
        const data = { identity: workKey, cursor: cursor || ""};
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            // if(data?.cursor) {
            //     this.fetchListClosed(workKey, data.cursor);
            // }

            return data.cursor;
        }
        return null;
    };

    async fetchListMentor(workKey: string, cursor?: CursorType): Promise<FHRequest[]>  {
        const url = "/request/list/mentor";
        const data = { identity: workKey, cursor: cursor || ""};
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(data?.cursor) {
                this.fetchListMentor(workKey, data.cursor);
            }
            return data.cursor;
        }
        return [];
    };

    async fetchListClaimedByManager(manager_identity: string, assistant_identity: string, cursor?: CursorType): Promise<FHRequestType[]>  {
        const url = "/request/list/claimed/by-manager";
        const data = { identity: manager_identity,
            assistant_identity: assistant_identity,
            cursor: cursor || ""};
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(data?.cursor) {
                this.fetchListClaimedByManager(manager_identity, assistant_identity, data.cursor);
            }
            // This returns as possibly incomplete list due to the cursor,
            // but FHRequestStore.items is complete when .then() is called
            // so you can/should just use that.
            return data.items;
        }
        return [];
    };


    async createRequest(identity: string, title: string, content: string, project: string) {
        const data = { identity, title, content, project };
        const response = await api_post("/request/create", data);
        if(response.ok) {
            const data = await response.json() as FHRequestType;
            this.receiveUpdate(data);
            return data;
        }
        return null;
    }


    async doClaim(identity: string, request: string) {
        const data = { identity, request };
        const response = await api_post("/request/claim", data);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data);
            return data;
        }
        throw "Unable to claim request"
    }

    async supportAssign(workKey: string, assign_user: string, request: string) {
        const data = { identity: workKey, assign_user, request };
        const response = await api_post("/request/support-assign", data);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data);
            return true;
        }
        return false;
    }

    async supportInvite(workKey: string, invite_user: string, request: string) {
        const data = { identity: workKey, invite_user, request };
        const response = await api_post("/request/support-invite", data);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data);
            return true;
        }
        return false;
    }

    async updateStatus(workKey: string, request: string, status: number, delayTime: string) {
        const data = { identity: workKey, request, status, delay: delayTime || '' };
        const response = await api_post("/request/update-status", data);
        console.log("updatestatus: ", response);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data);
            return data;
        }
        return null;
    }


    async supportUpdatePrice(workKey: string, request: string, credits: number, price: string) {
        const data = { identity: workKey, request, credits, price: parseFloat(price) };
        const response = await api_post("/request/support/update-price", data);
        console.log("update price: ", response);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data);
            return data;
        }
        return null;
    }

    async supportCloseQuietly(workKey: string, request: string) {
        const data = { identity: workKey, request };
        const response = await api_post("/request/support/close-quietly", data);
        console.log("closequietly: ", response);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data);
            return data;
        }
        return null;
    }

    async supportArchiveRequest(workKey: string, request: string) {
        const data = { identity: workKey, request };
        const response = await api_post("/request/support/archive", data);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data);
            return data;
        }
        return null;
    }

    modelConstructor() {
        return FHRequest;
    }

}
